import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ProductKeys } from '../../../../common/constants/product-keys';
import { type ThirdPartyConfigsWithAPIResponse } from '../../../../common/constants/schemas/3p-config';
import { useSelectedDatasource, useSelectedProduct } from '../../../../controllers/store/search';

/**
 * Context that tracks the set of app keys that have been "promoted" from the
 * "Show more apps" dialog into the visible app filter pill bar. Promoted apps
 * are rendered as the first pills in the bar (before the regular visible
 * options) and are removed from the overflow dropdown. State is in-memory only
 * and intentionally does not persist across page reloads.
 */
type PromotedAppsContextValue = {
	promotedAppKeys: string[];
	promoteApp: (key: string) => void;
	// The specific Atlassian Web Crawler website (datasource) most recently selected. Kept here
	// (rather than derived from the live selection) so the web crawler pill keeps showing that
	// website's name — and stays re-selectable as that website — even after another app filter is
	// selected. In-memory only; does not persist across reloads.
	promotedWebCrawlerDatasource: ThirdPartyConfigsWithAPIResponse | undefined;
};

const PromotedAppsContext = createContext<PromotedAppsContextValue>({
	promotedAppKeys: [],
	promoteApp: () => {},
	promotedWebCrawlerDatasource: undefined,
});

/**
 * Provider for the promoted apps state. Place this around `LiteAppFilterBar`
 * and `LiteShowAllAppsButton` so they share the same in-memory list of
 * apps that have been selected from the "Show more apps" dialog.
 */
export const PromotedAppsProvider = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const [promotedAppKeys, setPromotedAppKeys] = useState<string[]>([]);
	const [promotedWebCrawlerDatasource, setPromotedWebCrawlerDatasource] = useState<
		ThirdPartyConfigsWithAPIResponse | undefined
	>(undefined);
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();

	const promoteApp = useCallback((key: string) => {
		setPromotedAppKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
	}, []);

	// Whenever a specific web crawler website is the active selection (chosen from the dialog or
	// restored from the URL), remember it so its pill keeps showing that website's name after the
	// user selects a different app filter. Functional update keeps the effect from re-running on
	// unrelated selection changes.
	useEffect(() => {
		if (selectedProduct === ProductKeys.AtlassianWebCrawler && selectedDatasource) {
			setPromotedWebCrawlerDatasource((prev) =>
				prev?.datasourceId === selectedDatasource.datasourceId ? prev : selectedDatasource,
			);
		}
	}, [selectedProduct, selectedDatasource]);

	const value = useMemo(
		() => ({ promotedAppKeys, promoteApp, promotedWebCrawlerDatasource }),
		[promotedAppKeys, promoteApp, promotedWebCrawlerDatasource],
	);

	return <PromotedAppsContext.Provider value={value}>{children}</PromotedAppsContext.Provider>;
};

export const usePromotedApps = (): PromotedAppsContextValue => useContext(PromotedAppsContext);
